import React, { type FC, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import { useDebounceValue, useIntersectionObserver } from 'usehooks-ts';

import { ProcessResponseDTOStatus, useGetProcessById, useGetProcessTalentsInfinite } from '@/api/generated';
import PlusIcon from '@/assets/icons/plus.svg?react';
import SearchIcon from '@/assets/icons/search.svg?react';
import EmptyState from '@/components/EmptyState';
import Field from '@/components/fields/Field';
import TextInput from '@/components/fields/TextInput';
import Form from '@/components/Form/Form';
import Loader from '@/components/Loader/Loader';
import LoadingButton from '@/components/LoadingButton';
import { useProcessPermissions } from '@/hooks/permissions/useProcessPermissions';
import { PROCESS_SETTINGS_STEP_KEYS, toProcessSettingsForm } from '@/root/linker';
import typography from '@/theme/typography';

import TalentCard from './TalentCard';

const CREATE_BUTTON_ALLOWED_PERMISSIONS = [
  ProcessResponseDTOStatus.ACTIVE,
  ProcessResponseDTOStatus.ON_HOLD,
  ProcessResponseDTOStatus.UNPUBLISHED
] as ProcessResponseDTOStatus[];

interface ProcessTalentsListProps {
  processId: string;
}

const ProcessTalentsList: FC<ProcessTalentsListProps> = ({ processId }) => {
  const navigate = useNavigate();
  const { data: process, isLoading: isProcessLoading } = useGetProcessById(processId);
  const { palette } = useTheme();
  const { t } = useTranslation(['processes', 'talents', 'common']);
  const { value: { canAddTalents } = {} } = useProcessPermissions({ variables: { processId } });

  // Simple form for search
  const form = useForm({ defaultValues: { q: '' } });
  const searchValue = form.watch('q');
  const [debouncedSearch] = useDebounceValue(searchValue, 300);

  // Build query params directly
  const queryParams = useMemo(
    () => ({
      q: debouncedSearch || undefined
    }),
    [debouncedSearch]
  );

  const {
    data: talentsData,
    isLoading: isTalentsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetProcessTalentsInfinite(processId, queryParams, {
    query: { enabled: !!processId, suspense: false }
  });

  const talents = useMemo(() => talentsData?.pages?.flatMap(page => page?.talents ?? []) ?? [], [talentsData]);

  // Use refs to access current values in the onChange callback without re-creating it
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  hasNextPageRef.current = hasNextPage;
  isFetchingNextPageRef.current = isFetchingNextPage;

  const handleIntersectionChange = useCallback(
    (isIntersecting: boolean) => {
      if (isIntersecting && hasNextPageRef.current && !isFetchingNextPageRef.current) {
        fetchNextPage();
      }
    },
    [fetchNextPage]
  );

  const { ref: loadMoreRef } = useIntersectionObserver({
    threshold: 0,
    onChange: handleIntersectionChange
  });

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingNextPage]);

  const isEmptyQuery = !debouncedSearch;
  const isLoading = !processId || isTalentsLoading;
  const isEmpty = !talents.length;

  const isCreateButtonVisible = Boolean(
    process?.status &&
      process?.talentsCount &&
      process.talentsCount > 0 &&
      canAddTalents &&
      CREATE_BUTTON_ALLOWED_PERMISSIONS.includes(process.status)
  );

  return (
    <Stack gap={1} p={2} height="100%">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Form form={form} sx={{ justifyContent: 'start', display: 'flex' }}>
          <Field
            component={TextInput}
            name="q"
            innerProps={{
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: 'grey.700' }}>
                    <SearchIcon />
                  </InputAdornment>
                ),
                placeholder: t('layouts.processes.details.info.search')
              }
            }}
          />
        </Form>
        {isCreateButtonVisible && (
          <Button
            variant="outlined"
            startIcon={<PlusIcon />}
            component={Link}
            to={toProcessSettingsForm({
              processId,
              tabKey: PROCESS_SETTINGS_STEP_KEYS.TALENTS
            })}
            sx={{ marginInlineStart: 3 }}
          >
            {t('layouts.processes.details.info.addTalent')}
          </Button>
        )}
      </Stack>
      <>
        {(isLoading || isProcessLoading) && (
          <Stack justifyContent="center" alignItems="center" height="100%">
            <Loader />
          </Stack>
        )}
        {!isLoading && !isProcessLoading && (isEmpty || !process) && (
          <Stack justifyContent="center" height="100%">
            <EmptyState
              imageUrl="/images/empty-talent-list-illustration.svg"
              imageAlt={t('talents:layouts.talent.talentEmptyAlt')}
              buttonLabel={t('layouts.processes.details.info.addTalent')}
              imageSx={{ width: 243 }}
              onCreate={
                canAddTalents
                  ? () =>
                      navigate(
                        toProcessSettingsForm({
                          processId,
                          tabKey: PROCESS_SETTINGS_STEP_KEYS.TALENTS
                        })
                      )
                  : undefined
              }
            >
              <Typography variant="body2" sx={{ fontWeight: typography.fontWeightMedium, color: palette.grey[700] }}>
                {t(`layouts.processes.details.info.${isEmptyQuery ? 'noTalents' : 'noTalentsSearch'}`)}
              </Typography>
              <Typography variant="body2" sx={{ color: palette.grey[700] }}>
                {t('layouts.processes.details.info.noTalentsSubtitle')}
              </Typography>
            </EmptyState>
          </Stack>
        )}
        {!isLoading &&
          !isProcessLoading &&
          !isEmpty &&
          process &&
          talents.map(talent => <TalentCard key={talent.id} talent={talent} process={process} showDropdown />)}
        {hasNextPage && (
          <Box ref={loadMoreRef} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <LoadingButton isLoading={isFetchingNextPage} onClick={handleLoadMore} variant="text">
              {t('common:actions.loadMore')}
            </LoadingButton>
          </Box>
        )}
      </>
    </Stack>
  );
};

export default ProcessTalentsList;
